import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect } from "react";
import { formatTimeVariableValue } from "../utils/timeDurationUtils";
import { formatProviderAttribution } from "../utils/providerAttribution";
import { createPortal } from "react-dom";
import {
  HeartIcon,
  ChatBubbleLeftEllipsisIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";
import { Post } from "../services/connections";
import UserChip from "./common/UserChip";
import ImageViewer from "./common/ImageViewer";
import {
  getActivityLog,
  getTrainingLog,
  getPerformanceLog,
} from "../services/dayPostService";
import {
  updatePost,
  deletePost,
  uploadPostImage,
  updatePostWithImage,
} from "../services/postService";
import { unfollowUser } from "../services/connections";
import heic2any from "heic2any";
import { getUserPublicData } from "../services/userService";
import { useUserProfiles, useUserProfile } from "../stores";
import {
  ActivityLog,
  ActivityHeartRateData,
  PositionData,
  HeartRateZone,
  PositionSample,
  Session,
  ActivityMeasurement,
  PerformanceLog,
} from "../types/post";
import { ActivityType } from "../types/activity";
import { sessionService } from "../services/sessionService";
import { getPrimarySessionID } from "../utils/sessionLinks";
import { activityService } from "../services/activityService";
import { GlobalActivityService } from "../services/globalActivityService";
import SessionActivitiesTable from "./sessions/SessionActivitiesTable";
import {
  addLikeToPost,
  removeLikeFromPost,
  addCommentToPost,
  editCommentOnPost,
  deleteCommentFromPost,
} from "../services/postService";
import { Comment } from "../services/connections";
import { Timestamp } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { getFunctions, httpsCallable } from "firebase/functions";

// Global state to prevent duplicate unfollow toasts and operations
let globalUnfollowInProgress: { [key: string]: boolean } = {};
let globalUnfollowDebounce: { [key: string]: NodeJS.Timeout } = {};

interface PostCardProps {
  post: Post & { id: string };
  userId: string;
  pageLoading?: boolean;
  currentUserId?: string; // ID of the currently logged-in user
  showOwnerActions?: boolean; // Whether to show edit/delete actions (for profile page)
  onPostUpdate?: () => void; // Callback to refetch data after update
  onPostDelete?: () => void; // Callback to refetch data after delete
  onFeedRefresh?: () => void; // Callback to refresh feed after unfollow
  // Fired the moment the user confirms an unfollow, before the server round-trip.
  // Lets the parent optimistically hide posts from this user so the action
  // feels instant.
  onUnfollowOptimistic?: (unfollowedUserId: string) => void;
  onLikeChange?: (
    postId: string,
    userId: string,
    action: "like" | "unlike",
  ) => void;
  onCommentChange?: (
    postId: string,
    updatedComment: Partial<Comment>, // allow partial since delete only sends id
    action: "create" | "edit" | "delete",
  ) => void;
}

// Stable Modal component defined outside PostCard to prevent unmount/remount on re-renders
const Modal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return createPortal(children, document.body);
};

const PostCard: React.FC<PostCardProps> = ({
  post,
  userId,
  currentUserId,
  showOwnerActions = false,
  onPostUpdate,
  onPostDelete,
  onFeedRefresh,
  onUnfollowOptimistic,
  pageLoading = false,
  onCommentChange,
  onLikeChange,
}) => {
  const navigate = useNavigate();
  const profileStore = useUserProfiles();
  const { profile: currentUserProfile } = useUserProfile(currentUserId || null);
  const [activityLog, setActivityLog] = useState<ActivityLog | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, ActivityType[]>
  >({});
  const [loading, setLoading] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [imageError, setImageError] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPrivate, setIsPrivate] = useState(post.isPrivate || false); // Track privacy state
  const [isGPSPrivate, setIsGPSPrivate] = useState(
    post.gpsDataIsPrivate || false,
  ); // Track GPS privacy state
  const [isTogglingPrivacy, setIsTogglingPrivacy] = useState(false); // Track privacy toggle loading
  const [isTogglingGPSPrivacy, setIsTogglingGPSPrivacy] = useState(false); // Track GPS privacy toggle loading
  const [linkedActivityLogID, setLinkedActivityLogID] = useState<string | null>(
    null,
  );
  const [linkedSessionID, setLinkedSessionID] = useState<string | null>(null);
  const [performanceLog, setPerformanceLog] = useState<PerformanceLog | null>(
    null,
  );
  const [imageSlideHeight, setImageSlideHeight] = useState<number | null>(null);
  const [reportReasons, setReportReasons] = useState<{ value: string; label: string }[]>([]);

  // Create unified media array (GPS map + images)
  const resolvedActivityLogID = post.activityLogID || linkedActivityLogID;
  const resolvedSessionID = post.sessionID || linkedSessionID;
  const hasGPSData =
    activityLog?.positionData?.position_samples &&
    activityLog.positionData.position_samples.length > 0;
  const hasMediaImages = post.mediaList && post.mediaList.length > 0;
  const hasSessionDetails =
    !!session?.ActivityMeasurementList &&
    session.ActivityMeasurementList.length > 0;
  const hasHeartRateData =
    !!activityLog?.heartRateData?.summary?.hr_zone_data &&
    activityLog.heartRateData.summary.hr_zone_data.length > 0;

  // Check if current user can view GPS data (use local state for real-time updates)
  const isOwner = currentUserId === post.userID;
  const canViewGPS = isOwner || !isGPSPrivate; // Owner can always see, others only if not private
  const postOwnerId = post.userID || userId;
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportNote, setReportNote] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  // Build unified slides: images first, session second, then activity visuals
  const unifiedMediaSlides = (() => {
    const slides: Array<{
      type: "gps" | "heartRate" | "image" | "session";
      content: any;
    }> = [];

    if (hasMediaImages) {
      post.mediaList.forEach((imageUrl) => {
        slides.push({ type: "image", content: imageUrl });
      });
    }

    if (hasSessionDetails && !performanceLog) {
      slides.push({
        type: "session",
        content: {
          measurements: session.ActivityMeasurementList,
          sessionID: resolvedSessionID,
          sourceUserID: post.userID,
        },
      });
    }

    if (hasGPSData && canViewGPS) {
      slides.push({ type: "gps", content: activityLog });
    }

    if (hasHeartRateData) {
      slides.push({ type: "heartRate", content: activityLog });
    }

    return slides;
  })();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedNotes, setEditedNotes] = useState(post.notes || "");
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [newImagePreview, setNewImagePreview] = useState<string | null>(null);
  const [newImageError, setNewImageError] = useState<string | null>(null);
  const [imagePosition, setImagePosition] = useState(
    post.imagePosition || { x: 50, y: 50 },
  );
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0,
    posX: 50,
    posY: 50,
  });
  const imageContainerRef = React.useRef<HTMLDivElement>(null);
  const imageNaturalSize = React.useRef({ width: 0, height: 0 });
  const [isUnfollowing, setIsUnfollowing] = useState(false);
  const [showUnfollowConfirm, setShowUnfollowConfirm] = useState(false);
  const [userName, setUserName] = useState<string>("");
  const [userProfileImage, setUserProfileImage] = useState<string | null>(null);
  const [toastTimeoutId, setToastTimeoutId] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [isProcessingUnfollow, setIsProcessingUnfollow] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [likeLoading, setLikeLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null); // store commentId being deleted
  const [justSentComment, setJustSentComment] = useState(false);
  const [hasAttemptedGlobalFallback, setHasAttemptedGlobalFallback] =
    useState(false);
  const getRawActivityId = (activityTypeId: string): string => {
    if (activityTypeId.startsWith("global_")) {
      const parts = activityTypeId.split("_");
      if (parts.length >= 3) {
        return parts.slice(2).join("_");
      }
    }
    return activityTypeId;
  };

  const getGlobalCategoryIdFromActivityTypeId = (
    activityTypeId: string,
  ): string | undefined => {
    if (!activityTypeId.startsWith("global_")) return undefined;
    const parts = activityTypeId.split("_");
    if (parts.length >= 3) {
      return parts[1];
    }
    return undefined;
  };

  const getGlobalActivityLookupRef = (
    activity: string | ActivityMeasurement,
  ) => {
    const activityTypeId =
      typeof activity === "string" ? activity : activity.ActivityTypeID;
    const categoryId =
      typeof activity !== "string"
        ? activity.globalCategoryID ||
          getGlobalCategoryIdFromActivityTypeId(activityTypeId)
        : getGlobalCategoryIdFromActivityTypeId(activityTypeId);

    return {
      rawActivityId: getRawActivityId(activityTypeId),
      categoryId,
    };
  };

  const getActivityTypeName = (
    activity: string | ActivityMeasurement,
  ): string => {
    const { rawActivityId, categoryId } = getGlobalActivityLookupRef(activity);

    const userActivityType = activityTypes.find(
      (type) => type.id === rawActivityId,
    );
    if (userActivityType) return userActivityType.name;

    if (categoryId) {
      const categoryTypes = globalActivityTypes[categoryId] || [];
      const packActivityType = categoryTypes.find(
        (type) => type.id === rawActivityId,
      );
      if (packActivityType) return packActivityType.name;
    }

    // Fallback: search all loaded global categories by raw activity ID.
    for (const categoryTypes of Object.values(globalActivityTypes)) {
      const packActivityType = categoryTypes.find(
        (type) => type.id === rawActivityId,
      );
      if (packActivityType) return packActivityType.name;
    }

    return "Unknown Activity";
  };

  // Handle body overflow when dialogs are open
  useEffect(() => {
    const isDialogOpen =
      showDeleteConfirm || showUnfollowConfirm || showEditModal;
    if (isDialogOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showDeleteConfirm, showUnfollowConfirm, showEditModal]);

  // Cleanup toast timeout on unmount and prevent multiple toasts
  useEffect(() => {
    return () => {
      if (toastTimeoutId) {
        clearTimeout(toastTimeoutId);
      }
    };
  }, [toastTimeoutId]);

  // Reset toast state when component unmounts or userId changes
  useEffect(() => {
    return () => {
      const unfollowKey = `${currentUserId}-${userId}`;
      // console.log("Cleaning up PostCard component for:", unfollowKey);
      delete globalUnfollowInProgress[unfollowKey];

      // Clear debounce timeout
      if (globalUnfollowDebounce[unfollowKey]) {
        clearTimeout(globalUnfollowDebounce[unfollowKey]);
        delete globalUnfollowDebounce[unfollowKey];
      }

      // Clear toast timeout
      if (toastTimeoutId) {
        clearTimeout(toastTimeoutId);
        setToastTimeoutId(null);
      }
    };
  }, [userId, currentUserId, toastTimeoutId]);

  // Fallback lookup: if post doesn't carry direct links, resolve from linked training log.
  useEffect(() => {
    const fetchLinkedIdsFromTrainingLog = async () => {
      const needsActivityFallback = !post.activityLogID;
      const needsSessionFallback = !post.sessionID;

      if (
        !postOwnerId ||
        !post.trainingLogID ||
        (!needsActivityFallback && !needsSessionFallback)
      ) {
        setLinkedActivityLogID(null);
        setLinkedSessionID(null);
        return;
      }

      try {
        const trainingLog = await getTrainingLog(
          postOwnerId,
          post.trainingLogID,
        );
        setLinkedActivityLogID(trainingLog?.linkedActivityLogID || null);
        setLinkedSessionID(getPrimarySessionID(trainingLog) || null);
      } catch (error) {
        console.error("Error fetching linked IDs from training log:", error);
        setLinkedActivityLogID(null);
        setLinkedSessionID(null);
      }
    };

    fetchLinkedIdsFromTrainingLog();
  }, [postOwnerId, post.trainingLogID, post.activityLogID, post.sessionID]);

  // Fetch performance log via session's linkedPerformanceLogID or post's performanceLogID
  useEffect(() => {
    const fetchPerformanceLog = async () => {
      const perfLogID =
        session?.linkedPerformanceLogID || post.performanceLogID;
      if (!perfLogID || !postOwnerId) {
        setPerformanceLog(null);
        return;
      }
      try {
        const log = await getPerformanceLog(postOwnerId, perfLogID);
        setPerformanceLog(log);
      } catch (error) {
        console.error("Error fetching performance log:", error);
        setPerformanceLog(null);
      }
    };
    fetchPerformanceLog();
  }, [postOwnerId, session?.linkedPerformanceLogID, post.performanceLogID]);

  // Fetch activity log if activityLogID exists
  useEffect(() => {
    const fetchActivityLog = async () => {
      if (!resolvedActivityLogID || !postOwnerId) return;

      try {
        setLoading(true);
        const activity = await getActivityLog(
          postOwnerId,
          resolvedActivityLogID,
        );
        setActivityLog(activity);
      } catch (error) {
        console.error("Error fetching activity log:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivityLog();
  }, [resolvedActivityLogID, postOwnerId]);

  // Fetch session if sessionID exists
  useEffect(() => {
    const fetchSession = async () => {
      if (!resolvedSessionID || !postOwnerId) return;

      try {
        setLoading(true);
        const sessionData = await sessionService.getSession(
          postOwnerId,
          resolvedSessionID,
        );
        setSession(sessionData);

        // Also fetch activity types to display session measurements properly
        if (sessionData) {
          const activityTypesData =
            await activityService.fetchActivityTypes(postOwnerId);

          // Recover missing activity names by loading specific IDs directly.
          // This helps with historical sessions where activity types were soft-deleted.
          const sessionMeasurements = sessionData.ActivityMeasurementList || [];
          const requiredCustomActivityIds = Array.from(
            new Set(
              sessionMeasurements
                .filter((measurement) => !measurement.globalCategoryID)
                .map((measurement) =>
                  getRawActivityId(measurement.ActivityTypeID),
                ),
            ),
          );
          const loadedIds = new Set(
            activityTypesData.map((type) => type.id).filter(Boolean),
          );
          const missingIds = requiredCustomActivityIds.filter(
            (id) => !loadedIds.has(id),
          );

          if (missingIds.length > 0) {
            const recoveredTypes = await Promise.all(
              missingIds.map(async (activityTypeId) => {
                const typeRef = doc(
                  db,
                  "activityTypes",
                  postOwnerId,
                  "types",
                  activityTypeId,
                );
                const typeSnap = await getDoc(typeRef);
                if (!typeSnap.exists()) return null;
                return {
                  id: typeSnap.id,
                  ...(typeSnap.data() as Omit<ActivityType, "id">),
                } as ActivityType;
              }),
            );

            const merged = [
              ...activityTypesData,
              ...recoveredTypes.filter((type): type is ActivityType => !!type),
            ];
            const dedupedById = Array.from(
              new Map(
                merged
                  .filter((type) => !!type.id)
                  .map((type) => [type.id, type]),
              ).values(),
            );
            setActivityTypes(dedupedById);
          } else {
            setActivityTypes(activityTypesData);
          }
        }
      } catch (error) {
        console.error("Error fetching session:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [resolvedSessionID, postOwnerId]);

  useEffect(() => {
    if (!session?.ActivityMeasurementList?.length) return;

    const categoryIds = Array.from(
      new Set(
        session.ActivityMeasurementList.map((measurement) => {
          const directCategoryId = measurement.globalCategoryID;
          if (directCategoryId) return directCategoryId;
          return getGlobalCategoryIdFromActivityTypeId(
            measurement.ActivityTypeID,
          );
        }).filter((categoryId): categoryId is string => !!categoryId),
      ),
    );

    console.log("categoryIds", categoryIds);

    if (categoryIds.length === 0) return;

    const fetchGlobalTypes = async () => {
      try {
        const entries = await Promise.all(
          categoryIds.map(async (categoryId) => {
            const types =
              await GlobalActivityService.fetchActivityTypes(categoryId);
            const normalized: ActivityType[] = types.map((type) => ({
              id: type.id,
              name: type.name,
              description: type.description,
              variables: type.variables,
              image: type.image,
              isFromPack: true,
              categoryId: type.categoryId,
            }));
            return [categoryId, normalized] as const;
          }),
        );
        const nextFromCategoryFetch = Object.fromEntries(entries) as Record<
          string,
          ActivityType[]
        >;

        // Recover missing global activities directly by ID (including soft-deleted docs),
        // so historical sessions still resolve names.
        const measurements = session.ActivityMeasurementList || [];
        const requiredGlobalMap = new Map<
          string,
          { categoryId: string; rawActivityId: string }
        >();
        measurements.forEach((measurement) => {
          const categoryId =
            measurement.globalCategoryID ||
            getGlobalCategoryIdFromActivityTypeId(measurement.ActivityTypeID);
          if (!categoryId) return;
          const rawActivityId = getRawActivityId(measurement.ActivityTypeID);
          requiredGlobalMap.set(`${categoryId}::${rawActivityId}`, {
            categoryId,
            rawActivityId,
          });
        });
        const requiredGlobalRefs = Array.from(requiredGlobalMap.values());

        const missingRefs = requiredGlobalRefs.filter((ref) => {
          const loaded = nextFromCategoryFetch[ref.categoryId] || [];
          return !loaded.some((type) => type.id === ref.rawActivityId);
        });

        if (missingRefs.length > 0) {
          const recovered = await Promise.all(
            missingRefs.map(async (ref) => {
              const typeRef = doc(
                db,
                "adminActivityTypes",
                ref.categoryId,
                "types",
                ref.rawActivityId,
              );
              const typeSnap = await getDoc(typeRef);
              if (!typeSnap.exists()) return null;
              const data = typeSnap.data() as Omit<ActivityType, "id">;
              return {
                categoryId: ref.categoryId,
                type: {
                  id: typeSnap.id,
                  ...data,
                  isFromPack: true,
                  categoryId: ref.categoryId,
                } as ActivityType,
              };
            }),
          );

          recovered.forEach((entry) => {
            if (!entry) return;
            const list = nextFromCategoryFetch[entry.categoryId] || [];
            if (!list.some((type) => type.id === entry.type.id)) {
              nextFromCategoryFetch[entry.categoryId] = [...list, entry.type];
            }
          });
        }

        setGlobalActivityTypes((prev) => ({
          ...prev,
          ...nextFromCategoryFetch,
        }));
      } catch (error) {
        console.error(
          "Error fetching global activity types for post card:",
          error,
        );
      }
    };

    fetchGlobalTypes();
  }, [session]);

  useEffect(() => {
    setHasAttemptedGlobalFallback(false);
  }, [session?.id]);

  // Fallback: if any activity names are still unresolved, hydrate all global types.
  useEffect(() => {
    if (!session?.ActivityMeasurementList?.length) return;
    if (hasAttemptedGlobalFallback) return;

    const hasUnknownActivities = session.ActivityMeasurementList.some(
      (measurement) => getActivityTypeName(measurement) === "Unknown Activity",
    );
    if (!hasUnknownActivities) return;

    const fetchAllGlobalTypesForFallback = async () => {
      try {
        setHasAttemptedGlobalFallback(true);
        const categories = await GlobalActivityService.fetchCategories();
        const categoryIds = categories
          .map((category) => category.id)
          .filter((id): id is string => !!id);

        if (categoryIds.length === 0) return;

        const allTypes =
          await GlobalActivityService.fetchActivityTypesForCategories(
            categoryIds,
          );

        const groupedByCategory = allTypes.reduce<
          Record<string, ActivityType[]>
        >((acc, type) => {
          if (!type.categoryId) return acc;
          if (!acc[type.categoryId]) acc[type.categoryId] = [];
          acc[type.categoryId].push({
            id: type.id,
            name: type.name,
            description: type.description,
            variables: type.variables,
            image: type.image,
            isFromPack: true,
            categoryId: type.categoryId,
          });
          return acc;
        }, {});

        setGlobalActivityTypes((prev) => ({
          ...prev,
          ...groupedByCategory,
        }));
      } catch (error) {
        console.error(
          "Error fetching fallback global activity types for post card:",
          error,
        );
      }
    };

    fetchAllGlobalTypesForFallback();
  }, [session, activityTypes, globalActivityTypes, hasAttemptedGlobalFallback]);

  // Fetch user data for unfollow confirmation
  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;

      try {
        const userData = await getUserPublicData(userId);
        setUserName(userData?.username || "User");
        setUserProfileImage(
          (userData as any)?.profilePicture ||
            (userData as any)?.profileImage ||
            null,
        );
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUserName("User");
        setUserProfileImage(null);
      }
    };

    fetchUserData();
  }, [userId]);
  // Format timestamp for display
  const formatDate = (timestamp: any) => {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    // Check if it's today
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }
    // Check if it's yesterday
    else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    // Otherwise show full date
    else {
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  // Social media-style relative timestamps (e.g. "2h", "5m", "3d")
  const formatRelativeTime = (timestamp: any) => {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    const diffWeek = Math.floor(diffDay / 7);

    if (diffSec < 60) return "now";
    if (diffMin < 60) return `${diffMin}m`;
    if (diffHr < 24) return `${diffHr}h`;
    if (diffDay < 7) return `${diffDay}d`;
    if (diffWeek < 52) return `${diffWeek}w`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Text truncation constants and helpers
  const MAX_TEXT_LENGTH = 200;
  const shouldTruncateText = (text: string) =>
    text && text.length > MAX_TEXT_LENGTH;

  const getTruncatedText = (text: string) => {
    if (!shouldTruncateText(text)) return text;
    // Find the last space before MAX_TEXT_LENGTH to avoid cutting words
    const truncated = text.substring(0, MAX_TEXT_LENGTH);
    const lastSpaceIndex = truncated.lastIndexOf(" ");
    return lastSpaceIndex > 0
      ? truncated.substring(0, lastSpaceIndex)
      : truncated;
  };

  /**
   * Handle sharing a post with rich metadata and deep linking
   */
  const handleSharePost = async () => {
    try {
      // Create a shareable URL (deep link to the post)
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/post/${userId}/${post.id}`;

      // Create rich share content with post metadata
      const shareTitle = `${post.username}'s Training on PEAK^`;

      // Build share text with post details
      let shareText = `Check out this training post by ${post.username} on PEAK^!\n\n`;

      if (post.notes) {
        shareText += `"${post.notes.length > 100 ? post.notes.substring(0, 100) + "..." : post.notes}"\n\n`;
      }

      // Add training metrics if available
      const metrics = [];
      if (post.duration) {
        const hours = Math.floor(post.duration / 60);
        const mins = Math.round(post.duration % 60);
        metrics.push(
          `Duration: ${hours > 0 ? `${hours}h ${mins}m` : `${mins}m`}`,
        );
      }
      if (post.load) metrics.push(`Load: ${Math.round(post.load)}`);
      if (post.AverageHR)
        metrics.push(`Avg HR: ${Math.round(post.AverageHR)} bpm`);

      if (metrics.length > 0) {
        shareText += `📊 ${metrics.join(" • ")}\n\n`;
      }

      shareText += `🏃‍♀️ Join the PEAK^ community to track your training!`;

      // Check if native Web Share API is available (mobile devices)
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });

        // Show success toast
        showShareSuccessToast("Post shared successfully!");
      } else {
        // Fallback: Copy to clipboard (desktop browsers)
        const fullShareContent = `${shareTitle}\n\n${shareText}\n\n${shareUrl}`;
        await navigator.clipboard.writeText(fullShareContent);

        // Show success toast
        showShareSuccessToast("Post link copied to clipboard!");
      }
    } catch (error) {
      console.error("Error sharing post:", error);

      // Fallback: Copy just the URL
      try {
        const shareUrl = `${window.location.origin}/post/${userId}/${post.id}`;
        await navigator.clipboard.writeText(shareUrl);
        showShareSuccessToast("Post link copied to clipboard!");
      } catch (clipboardError) {
        console.error("Clipboard also failed:", clipboardError);
        showShareSuccessToast("Share failed. Please try again.", "error");
      }
    }
  };

  /**
   * Show a toast notification for share feedback
   */
  const fetchReportReasons = async () => {
  try {
    const functions = getFunctions();
    const getReportOptions = httpsCallable(functions, "getReportOptions");
    const result = await getReportOptions();
    const data = result.data as { reasons: { value: string; label: string }[] };
    setReportReasons(data.reasons ?? []);
  } catch (error) {
    console.error("Error fetching report reasons:", error);
  }
};
  const showShareSuccessToast = (
    message: string,
    type: "success" | "error" = "success",
  ) => {
    const toastId = `share-toast-${Date.now()}`;
    const toastElement = document.createElement("div");
    toastElement.id = toastId;
    toastElement.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background-color: ${type === "success" ? "rgba(34, 197, 94, 0.9)" : "rgba(239, 68, 68, 0.9)"};
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      z-index: 999999;
      animation: slideUpFade 0.3s ease-out forwards;
      pointer-events: none;
      white-space: nowrap;
      display: flex;
      align-items: center;
      gap: 8px;
    `;
    const icon = document.createElement("span");
    icon.textContent = type === "success" ? "✓" : "✕";
    icon.style.fontSize = "16px";
    const label = document.createElement("span");
    label.textContent = message;
    toastElement.append(icon, label);

    document.body.appendChild(toastElement);

    // Remove toast after 3 seconds
    setTimeout(() => {
      if (document.getElementById(toastId)) {
        document.body.removeChild(toastElement);
      }
    }, 3000);
  };

  const handleMenuAction = (
    action: "view" | "share" | "edit" | "delete" | "unfollow" | "toggleGPS" | "report",
  ) => {
    setIsMenuOpen(false);

    switch (action) {
      case "report":
        setReportReason("");
        setReportNote("");
        setReportSubmitted(false);
        setShowReportModal(true);
        if (reportReasons.length === 0) {
          fetchReportReasons(); // see below
        }
        break;
      case "view":
        navigate(`/post/${userId}/${post.id}`);
        break;
      case "share":
        handleSharePost();
        break;
      case "edit":
        setEditedNotes(post.notes || "");
        setShowEditModal(true);
        break;
      case "delete":
        setShowDeleteConfirm(true);
        break;
      case "unfollow":
        setShowUnfollowConfirm(true);
        break;
      case "toggleGPS":
        onToggleGPSPrivacy(post.id, userId);
        break;
    }
  };
  const handleSubmitReport = async () => {
  if (!currentUserId || !reportReason) return;
  setIsSubmittingReport(true);

  try {
    const functions = getFunctions();
    const submitReport = httpsCallable(functions, "submitReport");
    await submitReport({
      reportedUserId: userId,
      postId: post.id,
      reason: reportReason,
      note: reportNote.trim() || undefined,
    });
    setReportSubmitted(true);
    setTimeout(() => setShowReportModal(false), 1500);
  } catch (error) {
    console.error("Error submitting report:", error);
  } finally {
    setIsSubmittingReport(false);
  }
};
  const handleDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    // Call the delete function with post and user IDs
    onDeletePost(post.id, userId);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleUnfollowConfirm = () => {
    setShowUnfollowConfirm(false);
    handleUnfollow();
  };

  const handleUnfollowCancel = () => {
    setShowUnfollowConfirm(false);
  };

  /**
   * Delete post function - Deletes the post from the database
   * @param postId - The ID of the post to be deleted
   * @param userId - The ID of the user who owns the post
   */
  const onDeletePost = async (postId: string, userId: string) => {
    try {
      setIsDeleting(true);
      await deletePost(userId, postId);

      // Call the callback to refetch data
      if (onPostDelete) {
        onPostDelete();
      }

      // Close the confirmation dialog
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting post:", error);
      // You could add a toast notification here for error feedback
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Toggle post privacy - Updates the isPrivate field in the database
   * @param postId - The ID of the post
   * @param userId - The ID of the user who owns the post
   */
  const onTogglePrivacy = async (postId: string, userId: string) => {
    try {
      setIsTogglingPrivacy(true);
      const newPrivacyState = !isPrivate;

      // Update in Firestore
      await updatePost(userId, postId, undefined, newPrivacyState);

      // Update local state
      setIsPrivate(newPrivacyState);

      // Close menu
      setIsMenuOpen(false);

      // Show feedback
      showShareSuccessToast(
        newPrivacyState ? "Post is now private" : "Post is now public",
      );

      // Call the callback to refetch data if needed
      if (onPostUpdate) {
        onPostUpdate();
      }
    } catch (error) {
      console.error("Error toggling post privacy:", error);
      showShareSuccessToast("Failed to update privacy", "error");
    } finally {
      setIsTogglingPrivacy(false);
    }
  };

  /**
   * Toggle GPS data privacy - Updates the gpsDataIsPrivate field in the database
   * @param postId - The ID of the post
   * @param userId - The ID of the user who owns the post
   */
  const onToggleGPSPrivacy = async (postId: string, userId: string) => {
    try {
      setIsTogglingGPSPrivacy(true);
      const newGPSPrivacyState = !isGPSPrivate;

      // Update in Firestore
      await updatePost(
        userId,
        postId,
        undefined,
        undefined,
        newGPSPrivacyState,
      );

      // Update local state
      setIsGPSPrivate(newGPSPrivacyState);

      // Close menu
      setIsMenuOpen(false);

      // Show feedback
      showShareSuccessToast(
        newGPSPrivacyState
          ? "GPS data is now private"
          : "GPS data is now public",
      );

      // Call the callback to refetch data if needed
      if (onPostUpdate) {
        onPostUpdate();
      }
    } catch (error) {
      console.error("Error toggling GPS privacy:", error);
      showShareSuccessToast("Failed to update GPS privacy", "error");
    } finally {
      setIsTogglingGPSPrivacy(false);
    }
  };

  /**
   * Edit post function - Updates the post notes and image in the database
   * @param postId - The ID of the post to be edited
   * @param userId - The ID of the user who owns the post
   * @param newNotes - The updated notes content
   * @param newImageFile - Optional new image file to upload
   */
  const onEditPost = async (
    postId: string,
    userId: string,
    newNotes: string,
    newImageFile?: File,
  ) => {
    try {
      setIsUpdating(true);

      if (newImageFile) {
        // Update with new image and position
        await updatePostWithImage(
          userId,
          postId,
          newNotes,
          newImageFile,
          imagePosition,
        );
      } else {
        // Update notes and image position (if repositioned)
        const hasPositionChanged =
          post.imagePosition?.x !== imagePosition.x ||
          post.imagePosition?.y !== imagePosition.y;
        const posToSave =
          hasPositionChanged && post.mediaList && post.mediaList.length > 0
            ? imagePosition
            : undefined;
        await updatePost(
          userId,
          postId,
          newNotes,
          undefined,
          undefined,
          posToSave,
        );
      }

      // Update local state to reflect changes immediately
      setEditedNotes(newNotes);

      // Call the callback to refetch data
      if (onPostUpdate) {
        onPostUpdate();
      }

      // Close the modal and reset image state
      setShowEditModal(false);
      setNewImageFile(null);
      setNewImagePreview(null);
      setNewImageError(null);
    } catch (error) {
      console.error("Error updating post:", error);
      // You could add a toast notification here for error feedback
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditSave = () => {
    if (editedNotes.trim() === "") {
      // Could add validation here
      return;
    }
    onEditPost(post.id, userId, editedNotes.trim(), newImageFile || undefined);
  };

  const handleEditCancel = () => {
    setEditedNotes(post.notes || "");
    setNewImageFile(null);
    setNewImagePreview(null);
    setNewImageError(null);
    setShowEditModal(false);
  };

  const isHeicFile = (file: File): boolean => {
    const name = file.name.toLowerCase();
    return (
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      name.endsWith(".heic") ||
      name.endsWith(".heif")
    );
  };

  const handleImageSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (allow image/* and HEIC/HEIF)
      if (!file.type.startsWith("image/") && !isHeicFile(file)) {
        setNewImageError("Please select an image file");
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setNewImageError("Image size must be less than 10MB");
        return;
      }

      setNewImageError(null);

      // Convert HEIC/HEIF to JPEG
      if (isHeicFile(file)) {
        try {
          const convertedBlob = (await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.85,
          })) as Blob;

          const convertedFile = new File(
            [convertedBlob],
            file.name.replace(/\.heic$/i, ".jpg").replace(/\.heif$/i, ".jpg"),
            { type: "image/jpeg" },
          );

          setNewImageFile(convertedFile);
          setNewImagePreview(URL.createObjectURL(convertedBlob));
        } catch (err) {
          console.error("HEIC conversion failed:", err);
          setNewImageError(
            "Failed to convert HEIC image. Please try a JPG or PNG.",
          );
        }
        return;
      }

      setNewImageFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setNewImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setNewImageFile(null);
    setNewImagePreview(null);
    setNewImageError(null);
    setImagePosition({ x: 50, y: 50 });
  };

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    imageNaturalSize.current = {
      width: img.naturalWidth,
      height: img.naturalHeight,
    };
  };

  const getClientPos = (e: React.MouseEvent | React.TouchEvent) => {
    if ("touches" in e) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getClientPos(e);
    setIsDraggingImage(true);
    setDragStart({
      x: pos.x,
      y: pos.y,
      posX: imagePosition.x,
      posY: imagePosition.y,
    });
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDraggingImage || !imageContainerRef.current) return;
    e.preventDefault();
    const pos = getClientPos(e);
    const container = imageContainerRef.current;
    const rect = container.getBoundingClientRect();
    const { width: natW, height: natH } = imageNaturalSize.current;
    if (!natW || !natH) return;

    const containerAspect = rect.width / rect.height;
    const imageAspect = natW / natH;

    const dx = pos.x - dragStart.x;
    const dy = pos.y - dragStart.y;

    let newX = dragStart.posX;
    let newY = dragStart.posY;

    if (imageAspect > containerAspect) {
      // Image is wider — can pan horizontally
      const scaledW = (imageAspect / containerAspect) * rect.width;
      const panRange = scaledW - rect.width;
      if (panRange > 0) {
        const pctDelta = (dx / panRange) * -100;
        newX = Math.max(0, Math.min(100, dragStart.posX + pctDelta));
      }
    } else {
      // Image is taller — can pan vertically
      const scaledH = (containerAspect / imageAspect) * rect.height;
      const panRange = scaledH - rect.height;
      if (panRange > 0) {
        const pctDelta = (dy / panRange) * -100;
        newY = Math.max(0, Math.min(100, dragStart.posY + pctDelta));
      }
    }

    setImagePosition({ x: newX, y: newY });
  };

  const handleDragEnd = () => {
    setIsDraggingImage(false);
  };

  const handleUnfollow = async () => {
    const unfollowKey = `${currentUserId}-${userId}`;

    // Clear any existing debounce for this user
    if (globalUnfollowDebounce[unfollowKey]) {
      clearTimeout(globalUnfollowDebounce[unfollowKey]);
      delete globalUnfollowDebounce[unfollowKey];
    }

    // Prevent multiple calls and duplicate processing
    if (
      !currentUserId ||
      isUnfollowing ||
      globalUnfollowInProgress[unfollowKey]
    ) {
      console.log("Unfollow blocked - already in progress or toast showing");
      return;
    }

    // Mark as in-progress synchronously so the optimistic UI removal (which
    // unmounts this card) can't cancel a duplicate-click guard.
    globalUnfollowInProgress[unfollowKey] = true;
    setIsUnfollowing(true);

    if (toastTimeoutId) {
      clearTimeout(toastTimeoutId);
      setToastTimeoutId(null);
    }

    // Capture values needed by the detached promise below — these must not
    // depend on this component being mounted, since we're about to unmount it.
    const capturedCurrentUserId = currentUserId;
    const capturedUserId = userId;
    const capturedUserName = userName;
    const capturedOnFeedRefresh = onFeedRefresh;

    // Kick off the actual unfollow network call BEFORE the optimistic UI
    // removal. This is intentionally NOT awaited so the in-flight promise
    // (and its toast / feed-refresh follow-up) survive this component being
    // unmounted by the parent's optimistic removal.
    (async () => {
      try {
        console.log("Starting unfollow process for:", capturedUserName);
        await unfollowUser(capturedCurrentUserId, capturedUserId);
        console.log("Unfollow successful for:", capturedUserName);

        // Create a global toast that survives component unmounting.
        const toastId = `unfollow-toast-${Date.now()}`;
        const toastElement = document.createElement("div");
        toastElement.id = toastId;
        toastElement.style.cssText = `
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background-color: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          z-index: 999999;
          animation: slideUpFade 0.3s ease-out forwards;
          pointer-events: none;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 8px;
        `;
        const icon = document.createElement("span");
        icon.textContent = "✓";
        icon.style.color = "#22c55e";
        icon.style.fontSize = "16px";
        const label = document.createElement("span");
        label.textContent = `Unfollowed ${capturedUserName}`;
        toastElement.append(icon, label);

        if (!document.querySelector("#unfollow-toast-styles")) {
          const styleElement = document.createElement("style");
          styleElement.id = "unfollow-toast-styles";
          styleElement.textContent = `
            @keyframes slideUpFade {
              0% {
                opacity: 0;
                transform: translateX(-50%) translateY(20px);
              }
              100% {
                opacity: 1;
                transform: translateX(-50%) translateY(0);
              }
            }
          `;
          document.head.appendChild(styleElement);
        }

        document.body.appendChild(toastElement);

        setTimeout(() => {
          if (document.getElementById(toastId)) {
            document.body.removeChild(toastElement);
          }
          delete globalUnfollowInProgress[unfollowKey];
        }, 3000);

        if (capturedOnFeedRefresh) {
          console.log("Refreshing feed after unfollow");
          setTimeout(() => {
            capturedOnFeedRefresh();
          }, 1000); // Delay to let toast show and unfollow process complete
        }
      } catch (error) {
        console.error("Error unfollowing user:", error);
        delete globalUnfollowInProgress[unfollowKey];
      }
    })();

    // Now optimistically tell the parent to hide this user's posts. This may
    // unmount the current PostCard, but the promise above is already in flight.
    onUnfollowOptimistic?.(userId);
  };

  // Add CSS animation for toast
  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeInSlide {
        0% {
          opacity: 0;
          transform: translateY(-20px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-fade-in {
        animation: fadeInSlide 0.3s ease-out forwards;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleToggleLike = async (userID: string) => {
    if (!currentUserId) return;

    const alreadyLiked = post.likesList?.includes(currentUserId);
    setLikeLoading(true);

    try {
      if (alreadyLiked) {
        await removeLikeFromPost(post.id, currentUserId, userID);

        // Optimistically update UI
        onLikeChange?.(post.id, currentUserId, "unlike");
      } else {
        await addLikeToPost(post.id, currentUserId, userID);

        // Optimistically update UI
        onLikeChange?.(post.id, currentUserId, "like");
      }
    } catch (error) {
      console.error("Error toggling like:", error);
    } finally {
      setLikeLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !currentUserId) return;
    setCommentLoading(true);
    setJustSentComment(true);

    try {
      await addCommentToPost(
        post.id,
        currentUserId,
        newComment.trim(),
        post.userID,
      );
      setNewComment("");
      onCommentChange?.(
        post.id,
        {
          commentId: crypto.randomUUID(),
          userId: currentUserId,
          text: newComment,
          timestamp: Timestamp.now(),
        },
        "create",
      );
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setCommentLoading(false);
      // Reset send animation after a delay
      setTimeout(() => setJustSentComment(false), 300);
    }
  };

  const handleSaveEditedComment = async (commentId: string) => {
    if (!editingCommentText.trim() || !currentUserId) return;
    setEditLoading(true);

    try {
      await editCommentOnPost(
        post.id,
        post.userID,
        commentId,
        editingCommentText.trim(),
      );

      onCommentChange?.(
        post.id,
        {
          commentId,
          userId: currentUserId,
          text: editingCommentText.trim(),
          timestamp: Timestamp.now(),
        },
        "edit",
      );

      setEditingCommentId(null);
      setEditingCommentText("");
    } catch (error) {
      console.error("Error editing comment:", error);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    setDeleteLoading(commentId);

    try {
      // 🗑️ Delete from Firestore
      await deleteCommentFromPost(post.userID, post.id, commentId);

      // 🔄 Update local state (remove from UI)
      onCommentChange?.(
        post.id,
        { commentId }, // only need id for deletion
        "delete",
      );
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setDeleteLoading(null);
    }
  };

  return (
    <div className="relative">
      {/* Loading overlay during deletion */}
      {isDeleting && (
        <div className="absolute inset-0 bg-base-100/90 backdrop-blur-sm flex flex-col items-center justify-center rounded-xl z-50">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-3 border-red-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm font-medium text-base-content font-grotesk">
              Deleting post...
            </p>
          </div>
        </div>
      )}

      {pageLoading && (
        <div className="absolute inset-0 bg-base-100/70 backdrop-blur-sm flex items-center justify-center rounded-xl z-10">
          <div className="animate-spin h-5 w-5 border-b-2 border-blue-600 rounded-full"></div>
        </div>
      )}
      {/* Actual post content below */}
      <div className={pageLoading ? "pointer-events-none opacity-60" : ""}>
        <div className="bg-base-100 shadow-sm mb-2 w-full mx-auto md:mx-0 md:rounded-xl md:overflow-hidden">
          {/* Header: user info and date */}
          <div className="flex pb-0 p-4 items-center justify-between mb-3">
            <UserChip userId={userId} />
            <div className="flex items-center gap-2">
              {/* Privacy indicator badge - only show if private */}
              {isPrivate && showOwnerActions && currentUserId === userId && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-base-200 text-base-content/80 border border-base-300">
                  Private
                </span>
              )}

              {/* GPS Privacy indicator badge - only show if GPS is private */}
              {hasGPSData &&
                isGPSPrivate &&
                showOwnerActions &&
                currentUserId === userId && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-700 border border-blue-200">
                    GPS Private
                  </span>
                )}

              <p className="text-xs text-base-content/50 font-grotesk">
                {formatDate(post.postTimestamp)}
              </p>

              {/* Menu Button - Always show but content varies */}
              <div className="relative">
                <button
                  ref={menuButtonRef}
                  onClick={() => {
                    if (!isMenuOpen && menuButtonRef.current) {
                      const rect = menuButtonRef.current.getBoundingClientRect();
                      setMenuPosition({
                        top: rect.bottom + 4,
                        right: window.innerWidth - rect.right,
                      });
                    }
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  className="p-1 text-base-content/50 hover:text-base-content/80 hover:bg-base-200 rounded-full transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 5v.01M12 12v.01M12 19v.01"
                    />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {isMenuOpen && menuPosition && (
                  <Modal>
                    {/* Backdrop to close menu */}
                    <div
                      className="fixed inset-0 z-[999998]"
                      onClick={() => setIsMenuOpen(false)}
                    />

                    {/* Menu Items — positioned via viewport coords so it's never clipped
                        by the post card's overflow-hidden wrapper */}
                    <div
                      className="fixed w-48 bg-base-100 rounded-lg shadow-lg border border-base-200 py-1"
                      style={{ top: menuPosition.top, right: menuPosition.right, zIndex: 999999 }}
                    >
                      <button
                        onClick={() => handleMenuAction("view")}
                        className="w-full px-3 py-2 text-left text-sm text-base-content/80 hover:bg-base-200 flex items-center gap-2 transition-colors"
                      >
                        View post
                      </button>
                      <button
                        onClick={() => handleMenuAction("share")}
                        className="w-full px-3 py-2 text-left text-sm text-base-content/80 hover:bg-base-200 flex items-center gap-2 transition-colors"
                      >
                        Share
                      </button>

                      {showOwnerActions && currentUserId === userId && (
                        <>
                          <button
                            onClick={() => onTogglePrivacy(post.id, userId)}
                            disabled={isTogglingPrivacy}
                            className="w-full px-3 py-2 text-left text-sm text-base-content/80 hover:bg-base-200 flex items-center gap-2 transition-colors disabled:opacity-50"
                          >
                            {isTogglingPrivacy
                              ? "Loading..."
                              : isPrivate
                                ? "Make Post Public"
                                : "Make Post Private"}
                          </button>

                          {/* GPS Privacy Toggle - Only show if post has GPS data */}
                          {hasGPSData && (
                            <button
                              onClick={() => handleMenuAction("toggleGPS")}
                              disabled={isTogglingGPSPrivacy}
                              className="w-full px-3 py-2 text-left text-sm text-base-content/80 hover:bg-base-200 flex items-center gap-2 transition-colors disabled:opacity-50"
                            >
                              {isTogglingGPSPrivacy
                                ? "Loading..."
                                : isGPSPrivate
                                  ? "Make GPS Public"
                                  : "Make GPS Private"}
                            </button>
                          )}

                          <button
                            onClick={() => handleMenuAction("edit")}
                            className="w-full px-3 py-2 text-left text-sm text-base-content/80 hover:bg-base-200 flex items-center gap-2 transition-colors"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => handleMenuAction("delete")}
                            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-500/10 flex items-center gap-2 transition-colors"
                          >
                            Delete
                          </button>
                        </>
                      )}

                      {/* Unfollow button for non-owner posts */}
                      {currentUserId && currentUserId !== userId && (
                        <button
                          onClick={() => handleMenuAction("unfollow")}
                          disabled={isUnfollowing}
                          className="w-full px-3 py-2 text-left text-sm text-orange-600 hover:bg-orange-500/10 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUnfollowing ? "Unfollowing..." : "Unfollow"}
                        </button>
                      )}

                      {currentUserId && currentUserId !== userId && (
                        <button
                          onClick={() => handleMenuAction("report")}
                          className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-500/10 flex items-center gap-2 transition-colors"
                        >
                          Report
                        </button>
                      )}
                    </div>
                  </Modal>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          {post.notes && (
            <div className="mb-3 pl-4 pr-4">
              <p className="text-sm text-base-content/80 whitespace-pre-wrap leading-relaxed transition-all duration-300 ease-in-out">
                {isTextExpanded ? (
                  <>
                    {post.notes}
                    {shouldTruncateText(post.notes) && (
                      <button
                        onClick={() => setIsTextExpanded(false)}
                        className="ml-1 text-xs text-base-content/60 hover:text-base-content/80 font-medium transition-colors font-grotesk lowercase"
                      >
                        read less
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {getTruncatedText(post.notes)}
                    {shouldTruncateText(post.notes) && (
                      <>
                        <span className="text-base-content/60">...</span>
                        <button
                          onClick={() => setIsTextExpanded(true)}
                          className="ml-1 text-xs text-base-content/60 hover:text-base-content/80 font-medium transition-colors font-grotesk lowercase"
                        >
                          read more
                        </button>
                      </>
                    )}
                  </>
                )}
              </p>
            </div>
          )}

          {/* GPS Privacy Placeholder - Show when GPS exists but is private */}
          {hasGPSData && !canViewGPS && (
            <div className="mb-3 px-4">
              <div className="bg-base-100 border border-base-200 rounded-lg p-6 text-center">
                <svg
                  className="h-10 w-10 mx-auto text-base-content/50 mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <p className="text-sm text-base-content/80 font-medium">
                  GPS data is private
                </p>
                <p className="text-xs text-base-content/60 mt-1">
                  Route information hidden by user
                </p>
              </div>
            </div>
          )}

          {/* Unified Media Carousel - GPS Map + Images */}
          {unifiedMediaSlides.length > 0 && (
            <div className="mb-3 relative">
              {/* Horizontal Scroll Container */}
              <div
                className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-4"
                style={{
                  scrollSnapType: "x mandatory",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
                onScroll={(e) => {
                  // Update current index based on scroll position
                  const container = e.currentTarget;
                  const scrollLeft = container.scrollLeft;
                  const slideWidth = container.offsetWidth - 32; // minus padding
                  const newIndex = Math.round(scrollLeft / slideWidth);
                  if (
                    newIndex !== currentImageIndex &&
                    newIndex >= 0 &&
                    newIndex < unifiedMediaSlides.length
                  ) {
                    setCurrentImageIndex(newIndex);
                  }
                }}
              >
                {unifiedMediaSlides.map((slide, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 snap-start"
                    style={{
                      width: "calc(100% - 0.2rem)",
                    }}
                  >
                    {slide.type === "gps" ? (
                      /* GPS Map Slide */
                      <div
                        className={`relative w-full overflow-hidden rounded-md bg-base-200 ${!imageSlideHeight ? "h-80 md:h-96" : ""}`}
                        style={
                          imageSlideHeight
                            ? { height: imageSlideHeight }
                            : undefined
                        }
                      >
                        {/* Route label with provider badge - top left corner */}
                        <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
                          <span className="text-sm font-medium text-base-content">
                            Route
                          </span>
                          {formatProviderAttribution(slide.content.provider, slide.content.deviceModel) && (
                            <span className="text-xs bg-black/60 text-white px-2 py-1 rounded">
                              {formatProviderAttribution(slide.content.provider, slide.content.deviceModel)}
                            </span>
                          )}
                        </div>
                        <div className="relative w-full h-full">
                          {(() => {
                            const samples =
                              slide.content.positionData?.position_samples ||
                              [];
                            const validSamples = samples.filter(
                              (s: any) =>
                                s.coords_lat_lng_deg &&
                                s.coords_lat_lng_deg.length === 2 &&
                                !isNaN(s.coords_lat_lng_deg[0]) &&
                                !isNaN(s.coords_lat_lng_deg[1]),
                            );

                            if (validSamples.length === 0) {
                              return (
                                <div className="text-base-content/60 text-center py-16">
                                  No valid GPS data available
                                </div>
                              );
                            }

                            // Calculate bounding box
                            const lats = validSamples.map(
                              (s: any) => s.coords_lat_lng_deg[0],
                            );
                            const lngs = validSamples.map(
                              (s: any) => s.coords_lat_lng_deg[1],
                            );
                            const minLat = Math.min(...lats);
                            const maxLat = Math.max(...lats);
                            const minLng = Math.min(...lngs);
                            const maxLng = Math.max(...lngs);

                            const latRange = maxLat - minLat || 0.001;
                            const lngRange = maxLng - minLng || 0.001;
                            const padding = 0.1;
                            const paddedMinLat = minLat - latRange * padding;
                            const paddedMaxLat = maxLat + latRange * padding;
                            const paddedMinLng = minLng - lngRange * padding;
                            const paddedMaxLng = maxLng + lngRange * padding;

                            const avgLat = (minLat + maxLat) / 2;
                            const latMetersPerDegree = 111000;
                            const lngMetersPerDegree =
                              111000 * Math.cos((avgLat * Math.PI) / 180);

                            const latRangeMeters =
                              (paddedMaxLat - paddedMinLat) *
                              latMetersPerDegree;
                            const lngRangeMeters =
                              (paddedMaxLng - paddedMinLng) *
                              lngMetersPerDegree;

                            const containerWidth = 400;
                            const containerHeight = 200;
                            const gpsAspectRatio =
                              lngRangeMeters / latRangeMeters;
                            const containerAspectRatio =
                              containerWidth / containerHeight;

                            let viewBoxWidth = containerWidth;
                            let viewBoxHeight = containerHeight;
                            let offsetX = 0;
                            let offsetY = 0;

                            if (gpsAspectRatio > containerAspectRatio) {
                              viewBoxHeight = containerWidth / gpsAspectRatio;
                              offsetY = (containerHeight - viewBoxHeight) / 2;
                            } else {
                              viewBoxWidth = containerHeight * gpsAspectRatio;
                              offsetX = (containerWidth - viewBoxWidth) / 2;
                            }

                            const gpxToSvg = (
                              lat: number,
                              lng: number,
                            ): [number, number] => {
                              const x =
                                offsetX +
                                ((lng - paddedMinLng) /
                                  (paddedMaxLng - paddedMinLng)) *
                                  viewBoxWidth;
                              const y =
                                offsetY +
                                ((paddedMaxLat - lat) /
                                  (paddedMaxLat - paddedMinLat)) *
                                  viewBoxHeight;
                              return [x, y];
                            };

                            const pathPoints = validSamples.map(
                              (sample: any) => {
                                const [x, y] = gpxToSvg(
                                  sample.coords_lat_lng_deg[0],
                                  sample.coords_lat_lng_deg[1],
                                );
                                return `${x.toFixed(2)},${y.toFixed(2)}`;
                              },
                            );

                            const pathData =
                              pathPoints.length > 0
                                ? `M${pathPoints[0]} ${pathPoints
                                    .slice(1)
                                    .map((p: string) => `L${p}`)
                                    .join(" ")}`
                                : "";

                            const startPoint =
                              validSamples.length > 0
                                ? gpxToSvg(
                                    validSamples[0].coords_lat_lng_deg[0],
                                    validSamples[0].coords_lat_lng_deg[1],
                                  )
                                : null;
                            const endPoint =
                              validSamples.length > 1
                                ? gpxToSvg(
                                    validSamples[validSamples.length - 1]
                                      .coords_lat_lng_deg[0],
                                    validSamples[validSamples.length - 1]
                                      .coords_lat_lng_deg[1],
                                  )
                                : null;

                            return (
                              <svg
                                className="w-full h-full text-base-content"
                                viewBox={`0 0 ${containerWidth} ${containerHeight}`}
                                preserveAspectRatio="xMidYMid meet"
                              >
                                <rect
                                  width={containerWidth}
                                  height={containerHeight}
                                  fill="transparent"
                                />
                                {pathData && (
                                  <path
                                    d={pathData}
                                    stroke="currentColor"
                                    strokeWidth="3"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                )}
                                {startPoint && (
                                  <circle
                                    cx={startPoint[0]}
                                    cy={startPoint[1]}
                                    r="4"
                                    fill="#22c55e"
                                    stroke="white"
                                    strokeWidth="2"
                                  />
                                )}
                                {endPoint && startPoint && (
                                  <circle
                                    cx={endPoint[0]}
                                    cy={endPoint[1]}
                                    r="4"
                                    fill="#ef4444"
                                    stroke="white"
                                    strokeWidth="2"
                                  />
                                )}
                              </svg>
                            );
                          })()}
                        </div>
                      </div>
                    ) : slide.type === "heartRate" ? (
                      /* Heart Rate Zones Slide */
                      <div
                        className={`w-full overflow-hidden rounded-md bg-base-200 border border-base-300 p-3 ${!imageSlideHeight ? "h-80 md:h-96" : ""}`}
                        style={
                          imageSlideHeight
                            ? { height: imageSlideHeight }
                            : undefined
                        }
                      >
                        <div className="h-full overflow-y-auto pr-1">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <h3 className="text-sm font-medium text-base-content">
                                Heart Rate Zones
                              </h3>
                              {formatProviderAttribution(slide.content.provider, slide.content.deviceModel) && (
                                <span className="text-xs bg-base-100 text-base-content/80 px-2 py-1 rounded">
                                  {formatProviderAttribution(slide.content.provider, slide.content.deviceModel)}
                                </span>
                              )}
                            </div>
                            {slide.content.heartRateData.summary
                              ?.avg_hr_bpm && (
                              <div className="text-right">
                                <div className="text-xl font-bold text-base-content">
                                  {Math.round(
                                    slide.content.heartRateData.summary
                                      .avg_hr_bpm,
                                  )}
                                </div>
                                <div className="text-xs text-base-content/60">
                                  bpm (avg)
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Heart Rate Timeline */}
                          {slide.content.heartRateData.detailed?.hr_samples &&
                            slide.content.heartRateData.detailed.hr_samples
                              .length > 0 && (
                              <div className="mb-4">
                                <div className="relative h-32">
                                  <svg
                                    className="w-full h-full"
                                    viewBox="0 0 1000 120"
                                    preserveAspectRatio="none"
                                  >
                                    <rect
                                      width="1000"
                                      height="120"
                                      fill="transparent"
                                    />

                                    {(() => {
                                      const zones =
                                        slide.content.heartRateData?.summary
                                          ?.hr_zone_data || [];

                                      const getZoneColor = (
                                        zoneNumber: number | undefined,
                                        zoneName: string | undefined,
                                      ) => {
                                        switch (zoneNumber) {
                                          case 4:
                                            return "#ef4444";
                                          case 3:
                                            return "#f59e0b";
                                          case 2:
                                            return "#10b981";
                                          case 1:
                                          case 0:
                                            return "#3b82f6";
                                          default: {
                                            const lowerName = (
                                              zoneName || ""
                                            ).toLowerCase();
                                            if (
                                              lowerName.includes("peak") ||
                                              lowerName.includes("anaerobic")
                                            )
                                              return "#ef4444";
                                            if (
                                              lowerName.includes("vigorous") ||
                                              lowerName.includes("threshold")
                                            )
                                              return "#f59e0b";
                                            if (
                                              lowerName.includes("moderate") ||
                                              lowerName.includes("aerobic")
                                            )
                                              return "#10b981";
                                            if (
                                              lowerName.includes("light") ||
                                              lowerName.includes("easy")
                                            )
                                              return "#3b82f6";
                                            return "#6b7280";
                                          }
                                        }
                                      };

                                      return zones.map(
                                        (zone: any, zoneIndex: number) => {
                                          if (
                                            !zone.start_percentage ||
                                            !zone.end_percentage
                                          )
                                            return null;
                                          const startY =
                                            (100 - zone.end_percentage) * 1.2;
                                          const endY =
                                            (100 - zone.start_percentage) * 1.2;
                                          const height = endY - startY;

                                          return (
                                            <rect
                                              key={zoneIndex}
                                              x="0"
                                              y={startY}
                                              width="1000"
                                              height={height}
                                              fill={getZoneColor(
                                                zone.zone,
                                                zone.name,
                                              )}
                                              opacity="0.1"
                                            />
                                          );
                                        },
                                      );
                                    })()}

                                    {(() => {
                                      const samples =
                                        slide.content.heartRateData?.detailed
                                          ?.hr_samples || [];
                                      const validSamples = samples.filter(
                                        (s: any) => s.bpm && s.timestamp,
                                      );

                                      if (validSamples.length === 0)
                                        return null;

                                      const minHR = Math.min(
                                        ...validSamples.map((s: any) => s.bpm),
                                      );
                                      const maxHR = Math.max(
                                        ...validSamples.map((s: any) => s.bpm),
                                      );
                                      const hrRange = maxHR - minHR || 1;

                                      const points = validSamples
                                        .map(
                                          (sample: any, pointIndex: number) => {
                                            const x =
                                              (pointIndex /
                                                (validSamples.length - 1)) *
                                              1000;
                                            const y =
                                              120 -
                                              ((sample.bpm - minHR) / hrRange) *
                                                100 -
                                              10;
                                            return `${x},${y}`;
                                          },
                                        )
                                        .join(" ");

                                      return (
                                        <polyline
                                          fill="none"
                                          stroke="#374151"
                                          strokeWidth="2"
                                          points={points}
                                        />
                                      );
                                    })()}
                                  </svg>
                                </div>
                                <div className="flex justify-between text-xs text-base-content/60 mt-2">
                                  <span>Start</span>
                                  <span>End</span>
                                </div>
                              </div>
                            )}

                          <div className="space-y-2">
                            {slide.content.heartRateData.summary.hr_zone_data
                              .filter(
                                (zone: any) =>
                                  zone.duration_seconds &&
                                  zone.duration_seconds > 0,
                              )
                              .sort(
                                (a: any, b: any) =>
                                  (b.zone || 0) - (a.zone || 0),
                              )
                              .map((zone: any, sortedIndex: number) => {
                                const totalDuration =
                                  slide.content.heartRateData?.summary?.hr_zone_data?.reduce(
                                    (sum: number, z: any) =>
                                      sum + (z.duration_seconds || 0),
                                    0,
                                  ) || 1;
                                const percentage = Math.round(
                                  ((zone.duration_seconds || 0) /
                                    totalDuration) *
                                    100,
                                );
                                const duration = formatDuration(
                                  (zone.duration_seconds || 0) / 60,
                                );

                                const getZoneColorAndName = (
                                  zoneNumber: number | undefined,
                                  zoneName: string | undefined,
                                ) => {
                                  switch (zoneNumber) {
                                    case 4:
                                      return { color: "#ef4444", name: "Peak" };
                                    case 3:
                                      return {
                                        color: "#f59e0b",
                                        name: "Vigorous",
                                      };
                                    case 2:
                                      return {
                                        color: "#10b981",
                                        name: "Moderate",
                                      };
                                    case 1:
                                    case 0:
                                      return {
                                        color: "#3b82f6",
                                        name: "Light",
                                      };
                                    default: {
                                      const lowerName = (
                                        zoneName || ""
                                      ).toLowerCase();
                                      if (
                                        lowerName.includes("peak") ||
                                        lowerName.includes("anaerobic")
                                      )
                                        return {
                                          color: "#ef4444",
                                          name: "Peak",
                                        };
                                      if (
                                        lowerName.includes("vigorous") ||
                                        lowerName.includes("threshold")
                                      )
                                        return {
                                          color: "#f59e0b",
                                          name: "Vigorous",
                                        };
                                      if (
                                        lowerName.includes("moderate") ||
                                        lowerName.includes("aerobic")
                                      )
                                        return {
                                          color: "#10b981",
                                          name: "Moderate",
                                        };
                                      if (
                                        lowerName.includes("light") ||
                                        lowerName.includes("easy")
                                      )
                                        return {
                                          color: "#3b82f6",
                                          name: "Light",
                                        };
                                      return {
                                        color: "#6b7280",
                                        name: `Zone ${zoneNumber || "Unknown"}`,
                                      };
                                    }
                                  }
                                };

                                const { color, name } = getZoneColorAndName(
                                  zone.zone,
                                  zone.name,
                                );

                                return (
                                  <div
                                    key={`${zone.zone}-${sortedIndex}`}
                                    className="flex items-center space-x-3"
                                  >
                                    <div className="flex items-center space-x-2 w-24">
                                      <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: color }}
                                      />
                                      <span className="text-sm text-base-content/80">
                                        {zone.name || name}
                                      </span>
                                    </div>
                                    <div className="flex-1 flex items-center space-x-3">
                                      <div className="text-xs text-base-content/80 w-8">
                                        {percentage}%
                                      </div>
                                      <div className="flex-1 bg-base-100 rounded-full h-2">
                                        <div
                                          className="h-2 rounded-full"
                                          style={{
                                            backgroundColor: color,
                                            width: `${percentage}%`,
                                          }}
                                        />
                                      </div>
                                      <div className="text-xs text-base-content/80 w-12">
                                        {duration}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    ) : slide.type === "image" ? (
                      /* Regular Image Slide */
                      <div className="relative w-full overflow-hidden rounded-md">
                        {!loadedImages.has(index) && !imageError && (
                          <div className="w-full aspect-square bg-base-200 animate-pulse flex items-center justify-center">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-8 h-8 border-2 border-base-300 border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-sm text-base-content/60 font-grotesk">
                                Loading image...
                              </span>
                            </div>
                          </div>
                        )}
                        <img
                          src={slide.content}
                          alt={`Training post ${index + 1}`}
                          loading="eager"
                          decoding="async"
                          className={`w-full h-auto max-h-[80vh] object-contain transition-opacity duration-300 cursor-pointer hover:opacity-95 select-none ${
                            loadedImages.has(index)
                              ? "opacity-100"
                              : "opacity-0 absolute inset-0 h-full"
                          }`}
                          onLoad={(e) => {
                            setLoadedImages((prev) => new Set(prev).add(index));
                            if (
                              !imageSlideHeight &&
                              e.currentTarget.clientHeight > 0
                            ) {
                              setImageSlideHeight(e.currentTarget.clientHeight);
                            }
                          }}
                          onError={() => {
                            setLoadedImages((prev) => new Set(prev).add(index));
                            setImageError(true);
                          }}
                          onClick={() =>
                            !imageError && setImageViewerOpen(true)
                          }
                          draggable={false}
                        />
                        {imageError && index === currentImageIndex && (
                          <div className="w-full aspect-square bg-base-200 flex items-center justify-center border-2 border-dashed border-base-300 rounded-md">
                            <div className="flex flex-col items-center gap-2 text-base-content/60">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-12 w-12"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              <span className="text-sm font-grotesk">
                                Failed to load image
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Session Details Slide */
                      <div
                        className={`w-full rounded-md bg-base-200 border border-base-300 p-4 flex flex-col ${imageSlideHeight ? "overflow-hidden" : ""} ${!imageSlideHeight ? "h-80 md:h-96" : ""}`}
                        style={
                          imageSlideHeight
                            ? { height: imageSlideHeight }
                            : undefined
                        }
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-geist font-light text-base-content/60">
                            Session Details
                          </h3>
                          {slide.content.sessionID && (
                            <button
                              onClick={() =>
                                navigate(
                                  `/sessions?copyUID=${encodeURIComponent(slide.content.sourceUserID)}&copySessionID=${encodeURIComponent(slide.content.sessionID)}`,
                                )
                              }
                              className="text-xs font-geist px-4 py-1 rounded-md bg-base-100 hover:bg-base-100/50 border border-base-content/10 text-base-content/70 hover:text-base-content hover:bg-base-100 transition-colors "
                            >
                              Copy Session
                            </button>
                          )}
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto pr-1">
                          <SessionActivitiesTable
                            measurements={slide.content.measurements}
                            getActivityName={getActivityTypeName}
                            hideTimeColumn
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Subtle Rounded Rectangle Indicators */}
              {unifiedMediaSlides.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                  {unifiedMediaSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className="group"
                      aria-label={`Go to slide ${index + 1}`}
                    >
                      <div
                        className={`h-1 rounded-full transition-all duration-300 ${
                          index === currentImageIndex
                            ? "w-6 bg-neutral"
                            : "w-6 bg-base-100/50 group-hover:bg-base-100/70"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Training Metrics */}
          {post.trainingLogID && (
            <div className="flex pl-4 pr-4 gap-2 mb-3">
              {post.duration !== undefined && (
                <div className="flex-1 bg-base-200 rounded-md p-2 text-center">
                  <div className="text-xs text-base-content/60">Duration</div>
                  <div className="text-sm font-semibold font-heading text-base-content">
                    {formatDuration(post.duration)}
                  </div>
                </div>
              )}
              {post.load !== undefined && (
                <div className="flex-1 bg-base-200 rounded-md p-2 text-center">
                  <div className="text-xs text-base-content/60">Load</div>
                  <div className="text-sm font-semibold font-heading text-base-content">
                    {Math.round(post.load)}
                  </div>
                </div>
              )}
              {post.AverageHR !== undefined && (
                <div className="flex-1 bg-base-200 rounded-md p-2 text-center">
                  <div className="text-xs text-base-content/60">Avg HR</div>
                  <div className="text-sm font-semibold font-heading text-base-content">
                    {Math.round(post.AverageHR)} bpm
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Performance Log */}
          {performanceLog &&
            (() => {
              // Prefer the activity stored directly on the performance log.
              // Otherwise fall back to the legacy linked session, then to the
              // performance log's manual fields.
              const logActivity = performanceLog.activity;
              const firstActivity =
                !logActivity && session
                  ? (session.ActivityMeasurementList?.find(
                      (m) => m.timeLogged !== undefined,
                    ) ?? session.ActivityMeasurementList?.[0])
                  : null;

              const activityName = logActivity
                ? logActivity.activityName
                : firstActivity
                  ? getActivityTypeName(firstActivity)
                  : performanceLog.performanceTitle;

              const loggedVariables = (
                logActivity
                  ? logActivity.variables
                  : firstActivity
                    ? firstActivity.variables
                    : []
              ).filter(
                (v) => v.measurement !== null && v.measurement !== undefined,
              );

              return (
                <div className="mx-4 mb-3 bg-amber-50 rounded-lg p-3">
                  <p className="text-xs font-semibold tracking-widest text-amber-700/70 uppercase mb-1.5">
                    Performance
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-neutral-900 font-heading">
                      {activityName}
                    </span>

                    {/* Activity-derived variables */}
                    {loggedVariables.length > 0 ? (
                      loggedVariables.map((v) => {
                        const timeDisplay = formatTimeVariableValue(
                          v.name,
                          v.measurement,
                          v.unit,
                        );
                        return (
                          <span
                            key={v.name}
                            className="flex items-baseline gap-1"
                          >
                            <span className="text-neutral-900 font-medium">
                              {timeDisplay ?? v.measurement}
                            </span>
                            {!timeDisplay && v.unit && (
                              <span className="text-neutral-500 text-sm">
                                {v.unit}
                              </span>
                            )}
                          </span>
                        );
                      })
                    ) : !logActivity && !firstActivity ? (
                      /* Fallback: performanceLog result */
                      <>
                        <span className="text-neutral-900">
                          {performanceLog.performanceResult}
                        </span>
                        <span className="text-neutral-500 text-sm">units</span>
                      </>
                    ) : null}

                    {/* Tags (PB, SB, etc.) */}
                    {performanceLog.tag && performanceLog.tag.length > 0 && (
                      <div className="flex gap-1">
                        {performanceLog.tag.map((t) => (
                          <span
                            key={t}
                            className="px-1.5 py-0.5 text-xs font-bold bg-amber-200 text-amber-800 rounded"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {performanceLog.notes ? (
                    <p className="text-sm text-neutral-500 mt-1">
                      {performanceLog.notes}
                    </p>
                  ) : null}
                </div>
              );
            })()}

          {/* Likes & Comments Section */}
          <div className="px-4 py-3 border-t border-base-200">
            {/* Likes & Comments Row */}
            <div
              className={`flex items-center justify-between${showComments ? " mb-3" : ""}`}
            >
              {/* Like Button */}
              <button
                onClick={
                  !likeLoading ? () => handleToggleLike(post.userID) : undefined
                }
                disabled={likeLoading}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors duration-200
                  ${
                    post.likesList?.includes(currentUserId ?? "")
                      ? "text-red-600  "
                      : "text-base-content/80 hover:text-red-600"
                  }
                  ${likeLoading ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
                aria-label="Toggle Like"
              >
                {likeLoading ? (
                  <span className="w-4 h-4 border-2 border-base-300 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <motion.div
                    key={
                      post.likesList?.includes(currentUserId ?? "")
                        ? "liked"
                        : "unliked"
                    }
                    initial={{ scale: 0.7 }}
                    animate={{ scale: 1.2 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <HeartIcon
                      className={`w-5 h-5 ${
                        post.likesList?.includes(currentUserId ?? "")
                          ? "fill-red-600 stroke-red-600"
                          : "stroke-current"
                      }`}
                    />
                  </motion.div>
                )}
                <span>{post.likesList?.length || 0}</span>
              </button>

              {/* Comment Toggle */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowComments((prev) => {
                      const willShow = !prev;

                      // If comments will be shown, batch fetch all comment user profiles
                      if (willShow && post.CommentsList?.length) {
                        const userIds = post.CommentsList.map(
                          (comment) => comment.userId,
                        )
                          .filter((id) => id !== currentUserId) // Don't fetch current user
                          .filter(
                            (id, index, arr) => arr.indexOf(id) === index,
                          ); // Remove duplicates

                        if (userIds.length > 0) {
                          profileStore.batchFetch(userIds);
                        }
                      }

                      return willShow;
                    });
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-base-content/80 hover:text-base-content  transition-colors duration-200 cursor-pointer"
                  aria-label="Toggle Comments"
                >
                  <ChatBubbleLeftEllipsisIcon className="w-5 h-5" />
                  <span>{post.CommentsList?.length || 0}</span>
                </button>
              </div>
            </div>

            {showComments && (
              <div className="pt-2 mt-0">
                {/* Comment input — always at top like Instagram */}
                {currentUserId ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleAddComment();
                    }}
                    className="flex items-center gap-3 px-4 py-3 border-b border-base-200"
                    aria-label="Add Comment Form"
                  >
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-neutral flex-shrink-0 flex items-center justify-center">
                      {currentUserProfile?.imageUrlTiny || currentUserProfile?.imageUrl ? (
                        <img
                          src={currentUserProfile.imageUrlTiny || currentUserProfile.imageUrl}
                          alt="Your avatar"
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <span className="text-xs font-semibold text-neutral-content">
                          {currentUserProfile?.username
                            ?.charAt(0)
                            .toUpperCase() || "?"}
                        </span>
                      )}
                    </div>
                    <input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="flex-1 text-[16px] sm:text-sm bg-transparent text-base-content placeholder:text-base-content/40 outline-none border-none focus:ring-0 py-1"
                      disabled={commentLoading}
                      aria-label="New Comment Input"
                    />
                    <button
                      type="submit"
                      className="text-sm font-semibold text-primary hover:text-primary-dark disabled:opacity-30 transition-opacity duration-150 flex-shrink-0"
                      disabled={!newComment.trim() || commentLoading}
                      aria-label="Post Comment"
                    >
                      {commentLoading ? (
                        <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin inline-block" />
                      ) : (
                        "Post"
                      )}
                    </button>
                  </form>
                ) : (
                  <p className="text-sm text-base-content/50 text-center py-3 px-4 border-b border-base-200 select-none">
                    <button
                      onClick={() => navigate("/login")}
                      className="text-primary font-semibold hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded"
                    >
                      Log in
                    </button>{" "}
                    to comment
                  </p>
                )}

                {/* Comments list */}
                {post.CommentsList?.length ? (
                  <div className="px-4 pt-2 pb-1">
                    <AnimatePresence mode="sync">
                      {post.CommentsList.map((comment) => {
                        const commentProfile = profileStore.getProfile(
                          comment.userId,
                        );
                        const commentUsername =
                          commentProfile?.username || "User";
                        const commentAvatar =
                          commentProfile?.imageUrlTiny || commentProfile?.imageUrl;

                        return (
                          <motion.div
                            key={comment.commentId}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            className="py-2.5 group"
                          >
                            <div className="flex gap-3">
                              {/* Avatar */}
                              <button
                                onClick={() =>
                                  navigate(`/profile/${comment.userId}`)
                                }
                                className="w-8 h-8 rounded-full overflow-hidden bg-neutral flex-shrink-0 flex items-center justify-center mt-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                                aria-label={`View ${commentUsername}'s profile`}
                              >
                                {commentAvatar ? (
                                  <img
                                    src={commentAvatar}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                ) : (
                                  <span className="text-xs font-semibold text-neutral-content">
                                    {commentUsername.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </button>

                              {/* Comment body */}
                              <div className="flex-1 min-w-0">
                                {editingCommentId === comment.commentId ? (
                                  /* Edit mode */
                                  <div className="flex items-center gap-2">
                                    <input
                                      value={editingCommentText}
                                      onChange={(e) =>
                                        setEditingCommentText(e.target.value)
                                      }
                                      className="flex-1 text-[16px] sm:text-sm border border-base-300 rounded-lg px-3 py-1.5 bg-base-100 text-base-content outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:border-primary"
                                      disabled={editLoading}
                                      aria-label="Edit comment"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() =>
                                        handleSaveEditedComment(
                                          comment.commentId,
                                        )
                                      }
                                      disabled={
                                        editLoading ||
                                        !editingCommentText.trim()
                                      }
                                      className="text-sm font-semibold text-primary hover:text-primary-dark disabled:opacity-40 transition-opacity duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded"
                                      aria-label="Save edited comment"
                                    >
                                      {editLoading ? (
                                        <span className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin inline-block" />
                                      ) : (
                                        "Save"
                                      )}
                                    </button>
                                    <button
                                      onClick={() => setEditingCommentId(null)}
                                      className="text-sm text-base-content/50 hover:text-base-content transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded"
                                      aria-label="Cancel editing"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  /* View mode — inline username + text like Instagram */
                                  <>
                                    <p className="text-sm text-base-content leading-relaxed break-words">
                                      <button
                                        onClick={() =>
                                          navigate(`/profile/${comment.userId}`)
                                        }
                                        className="font-semibold text-base-content hover:underline mr-1.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded"
                                      >
                                        {commentUsername}
                                      </button>
                                      {comment.text}
                                    </p>

                                    {/* Meta row: timestamp + actions */}
                                    <div className="flex items-center gap-3 mt-1">
                                      <span className="text-xs text-base-content/40 select-none">
                                        {formatRelativeTime(comment.timestamp)}
                                      </span>

                                      {currentUserId === comment.userId && (
                                        <>
                                          <button
                                            onClick={() => {
                                              setEditingCommentId(
                                                comment.commentId,
                                              );
                                              setEditingCommentText(
                                                comment.text,
                                              );
                                            }}
                                            className="text-xs font-semibold text-base-content/40 hover:text-base-content/70 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded"
                                            aria-label="Edit comment"
                                          >
                                            Edit
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleDeleteComment(
                                                comment.commentId,
                                              )
                                            }
                                            disabled={
                                              deleteLoading ===
                                              comment.commentId
                                            }
                                            className="text-xs font-semibold text-base-content/40 hover:text-red-500 disabled:opacity-40 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded"
                                            aria-label="Delete comment"
                                          >
                                            {deleteLoading ===
                                            comment.commentId ? (
                                              <span className="w-3 h-3 border-2 border-base-300 border-t-transparent rounded-full animate-spin inline-block" />
                                            ) : (
                                              "Delete"
                                            )}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                ) : (
                  <p className="text-sm text-base-content/40 text-center py-4 select-none">
                    No comments yet — be the first!
                  </p>
                )}
              </div>
            )}
          </div>

          {/* iOS-style Delete Confirmation Dialog */}
          {showDeleteConfirm && (
            <Modal>
              <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999999,
                  width: "100vw",
                  height: "100vh",
                }}
                onClick={handleDeleteCancel}
              >
                <div
                  className="bg-base-100 rounded-lg w-full max-w-xs mx-auto overflow-hidden shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Dialog Content */}
                  <div className="p-6 text-center">
                    <h3 className="text-lg font-medium text-base-content mb-2 font-heading">
                      Delete Post
                    </h3>
                    <p className="text-sm text-base-content/80 leading-relaxed">
                      Are you sure you want to delete this post? This action
                      cannot be undone.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="border-t border-base-200">
                    <button
                      onClick={handleDeleteConfirm}
                      disabled={isDeleting}
                      className="w-full py-3 text-red-600 font-medium text-base transition-colors border-b hover:bg-red-500/10 border-base-200 font-grotesk disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isDeleting && (
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                      )}
                      {isDeleting ? "Deleting..." : "Delete"}
                    </button>
                    <button
                      onClick={handleDeleteCancel}
                      className="w-full py-3 text-base-content font-semibold text-base hover:bg-base-200 transition-colors font-grotesk"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}

          {/* iOS-style Unfollow Confirmation Dialog */}
          {showUnfollowConfirm && (
            <Modal>
              <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999999,
                  width: "100vw",
                  height: "100vh",
                }}
                onClick={handleUnfollowCancel}
              >
                <div
                  className="bg-base-100 rounded-lg w-full max-w-xs mx-auto overflow-hidden shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Dialog Content */}
                  <div className="p-6 text-center">
                    {/* Profile Image */}
                    <div className="flex justify-center mb-4">
                      {userProfileImage ? (
                        <img
                          src={userProfileImage}
                          alt={userName}
                          loading="lazy"
                          decoding="async"
                          className="w-16 h-16 rounded-full object-cover border-2 border-base-200"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-neutral text-neutral-content flex items-center justify-center text-xl font-bold border-2 border-base-200">
                          {userName.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* Username */}
                    <h3 className="text-lg font-medium text-base-content mb-3 font-grotesk">
                      {userName}
                    </h3>

                    {/* Confirmation Message */}
                    <p className="text-sm text-base-content/80 leading-relaxed">
                      Are you sure you want to unfollow {userName}? You will no
                      longer see their posts in your feed.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="border-t border-base-200">
                    <button
                      onClick={handleUnfollowConfirm}
                      disabled={isUnfollowing}
                      className="w-full py-3 text-orange-600 font-medium text-base transition-colors border-b border-base-200 font-grotesk disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUnfollowing ? "Unfollowing..." : "Unfollow"}
                    </button>
                    <button
                      onClick={handleUnfollowCancel}
                      className="w-full py-3 text-base-content font-semibold text-base hover:bg-blue-500/10 transition-colors font-grotesk"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}

          {/* Edit Post Modal */}
          {showEditModal && (
            <Modal>
              <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-0 md:p-4"
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999999,
                }}
                onClick={handleEditCancel}
              >
                <style>{`
                  @media (min-width: 768px) {
                    .edit-post-modal { height: auto !important; max-height: 90vh !important; }
                  }
                `}</style>
                <div
                  className="edit-post-modal bg-base-100 w-full max-w-lg mx-auto overflow-hidden shadow-2xl md:rounded-xl flex flex-col"
                  style={{ height: "100dvh", maxHeight: "100dvh" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex justify-between items-center p-4 pb-2 md:p-6 md:pb-2 flex-shrink-0 bg-base-100 md:rounded-t-xl">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleEditCancel}
                        className="p-1 rounded-full hover:bg-base-300 bg-base-300 transition-colors"
                        style={{ touchAction: "manipulation" }}
                      >
                        <ChevronLeftIcon className="h-7 w-7 text-base-content/70" />
                      </button>
                      <h3 className="text-xl font-grotesk text-base-content">
                        Edit <strong>Post</strong>
                      </h3>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8 pt-4 flex-1 overflow-y-auto">
                    {/* Image Upload Section */}
                    <div className="mb-4">
                      {/* Current Image Display (draggable to reposition) */}
                      {!newImagePreview &&
                        post.mediaList &&
                        post.mediaList.length > 0 && (
                          <div className="mb-3">
                            <div
                              ref={imageContainerRef}
                              className="relative w-full h-80 md:h-96 rounded-md overflow-hidden select-none"
                              style={{
                                cursor: isDraggingImage ? "grabbing" : "grab",
                                touchAction: "none",
                              }}
                              onMouseDown={handleDragStart}
                              onMouseMove={handleDragMove}
                              onMouseUp={handleDragEnd}
                              onMouseLeave={handleDragEnd}
                              onTouchStart={handleDragStart}
                              onTouchMove={handleDragMove}
                              onTouchEnd={handleDragEnd}
                            >
                              <img
                                src={post.mediaList[0]}
                                alt="Current post"
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover pointer-events-none"
                                style={{
                                  objectPosition: `${imagePosition.x}% ${imagePosition.y}%`,
                                }}
                                onLoad={handleImageLoad}
                                draggable={false}
                              />
                              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 rounded-full">
                                <span className="text-xs text-white font-grotesk">
                                  Drag to reposition
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* New Image Preview */}
                      {newImagePreview && (
                        <div className="mb-3">
                          <div
                            ref={imageContainerRef}
                            className="relative w-full h-80 md:h-96 rounded-md overflow-hidden select-none"
                            style={{
                              cursor: isDraggingImage ? "grabbing" : "grab",
                              touchAction: "none",
                            }}
                            onMouseDown={handleDragStart}
                            onMouseMove={handleDragMove}
                            onMouseUp={handleDragEnd}
                            onMouseLeave={handleDragEnd}
                            onTouchStart={handleDragStart}
                            onTouchMove={handleDragMove}
                            onTouchEnd={handleDragEnd}
                          >
                            <img
                              src={newImagePreview}
                              alt="New post preview"
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover pointer-events-none"
                              style={{
                                objectPosition: `${imagePosition.x}% ${imagePosition.y}%`,
                              }}
                              onLoad={handleImageLoad}
                              draggable={false}
                            />
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/50 rounded-full">
                              <span className="text-xs text-white font-grotesk">
                                Drag to reposition
                              </span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage();
                              }}
                              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* File Input */}
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept="image/*,.heic,.heif"
                          onChange={handleImageSelect}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="flex w-full justify-center items-center gap-2 px-4 py-2 bg-base-200 text-base-content/80 rounded-lg hover:bg-base-300 transition-colors cursor-pointer font-medium text-sm"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          {newImageFile ? "Change Image" : "Select New Image"}
                        </label>

                        {/* {newImageFile && (
                          <button
                            onClick={handleRemoveImage}
                            className="px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-500/10 rounded-lg transition-colors font-medium"
                          >
                            Remove
                          </button>
                        )} */}
                      </div>
                    </div>

                    {/* Image Error Message */}
                    {newImageError && (
                      <div className="mb-4 p-3 bg-red-500/10 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">{newImageError}</p>
                      </div>
                    )}

                    {/* Notes Section */}
                    <div className="mb-4">
                      <textarea
                        id="edit-notes"
                        value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        placeholder="Share your training thoughts, experience, or notes..."
                        className="w-full h-60 p-4 bg-base-200 rounded-md focus:outline-none focus:ring-0 resize-none transition-all font-grotesk text-base-content text-[16px] sm:text-sm"
                        maxLength={500}
                      />
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-base-content/60">
                          {editedNotes.length}/500 characters
                        </span>
                        {editedNotes.length > 450 && (
                          <span className="text-xs text-orange-500">
                            {500 - editedNotes.length} characters remaining
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="p-8 pt-0 pb-12 flex-shrink-0">
                    <button
                      onClick={handleEditSave}
                      disabled={
                        editedNotes.trim() === "" ||
                        editedNotes.length > 500 ||
                        isUpdating
                      }
                      className="w-full py-4 px-6 font-bold text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed font-grotesk"
                      style={{ touchAction: "manipulation" }}
                    >
                      {isUpdating ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}
          {showReportModal && (
          <Modal>
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
              style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999999 }}
              onClick={() => !isSubmittingReport && setShowReportModal(false)}
            >
              <div
                className="bg-base-100 rounded-lg w-full max-w-sm mx-auto shadow-2xl flex flex-col overflow-hidden"
                style={{ maxHeight: "85vh" }}
                onClick={(e) => e.stopPropagation()}
              >
                {reportSubmitted ? (
                  <div className="text-center py-4 p-6">
                    <p className="text-base-content font-medium">Report submitted. Thank you.</p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-lg font-medium text-base-content font-heading px-6 pt-6 pb-3 flex-shrink-0">
                      Report Post
                    </h3>

                    {/* Scrollable body: reason list + note field */}
                    <div className="overflow-y-auto px-6 flex-1 min-h-0">
                      <div className="space-y-2 mb-4">
                        {reportReasons.map((r) => (
                          <button
                            key={r.value}
                            onClick={() => setReportReason(r.value)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                              reportReason === r.value
                                ? "bg-red-500/10 text-red-600 border border-red-200"
                                : "bg-base-200 text-base-content/80 hover:bg-base-300"
                            }`}
                          >
                            {r.label}
                          </button>
                        ))}
                      </div>

                      <textarea
                        value={reportNote}
                        onChange={(e) => setReportNote(e.target.value)}
                        placeholder="Additional details (optional)"
                        className="w-full h-20 p-3 bg-base-200 rounded-md text-sm mb-4 outline-none resize-none"
                        maxLength={500}
                      />
                    </div>

                    {/* Fixed footer: buttons never scroll out of view */}
                    <div className="px-6 pb-6 pt-3 flex-shrink-0">
                      <button
                        onClick={handleSubmitReport}
                        disabled={!reportReason || isSubmittingReport}
                        className="w-full py-3 bg-red-600 text-white rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {isSubmittingReport ? "Submitting..." : "Submit Report"}
                      </button>
                      <button
                        onClick={() => setShowReportModal(false)}
                        className="w-full py-3 text-base-content font-medium hover:bg-base-200 rounded-lg mt-2"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Modal>
        )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      {/* Image Viewer - Only for image slides, not GPS */}
      {post.mediaList && post.mediaList.length > 0 && (
        <ImageViewer
          imageUrl={
            unifiedMediaSlides[currentImageIndex]?.type === "image"
              ? unifiedMediaSlides[currentImageIndex].content
              : post.mediaList[0] // Fallback to first image
          }
          isOpen={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          altText={`Post by ${post.username || "User"} - Image ${currentImageIndex + 1}`}
        />
      )}
    </div>
  );
};

export default PostCard;
